/**
 * NoSQLDetector — Detection of NoSQL injection attempt indicators in
 * client-side outbound requests.
 *
 * Focuses on MongoDB query operator injection — the dominant NoSQL injection
 * vector in modern web applications. Detects two attack surfaces:
 *
 *   1. URL parameter bracket notation: field[$ne]=value, field[%24ne]=value
 *      Classic authentication bypass: ?password[$ne]=anything bypasses
 *      MongoDB equality checks if the server passes query params directly.
 *
 *   2. JSON request body: {"field": {"$ne": null}}, {"$where": "function()..."}
 *      Used when the app forwards JSON body fields into a MongoDB query.
 *
 * $where is treated as a separate critical variant because it allows arbitrary
 * JavaScript execution on the MongoDB server — not just data comparison bypass.
 *
 * CWE-943 | OWASP A05:2025 – Injection
 */

/**
 * $where operator — allows JS execution on the MongoDB server.
 * Matched separately from other operators to assign critical confidence.
 */
const MONGO_WHERE = /\$where\b/i;

/**
 * Core MongoDB comparison, logical, and evaluation operators used for
 * authentication bypass and data enumeration.
 */
const MONGO_OPERATORS = /^\$(?:ne|gt|lt|gte|lte|in|nin|exists|regex|options|or|and|not|nor|elemMatch|size|all|type|mod)$/i;

/**
 * URL-encoded '$' sign (%24) inside bracket notation: field[%24ne]=value.
 * Encoding the dollar sign is almost always an intentional filter bypass —
 * legitimate libraries send literal $ characters in bracket keys.
 */
const ENCODED_DOLLAR_BRACKET = /\[%24[a-z]+\]/i;

export class NoSQLDetector {
	/**
	 * Analyzes an outbound request for NoSQL injection indicators.
	 *
	 * @param {{ url: string, body?: string }} options
	 * @returns {{ detected: true,  type: 'NOSQL', variant: string,
	 *             evidence: string, confidence: 'high'|'medium' }
	 *          | { detected: false }}
	 */
	analyze({ url, body }) {
		if (url && typeof url === 'string') {
			const urlHit = this._scanUrl(url);
			if (urlHit) return urlHit;
		}

		if (body && typeof body === 'string' && body.length > 0) {
			const bodyHit = this._scanBody(body);
			if (bodyHit) return bodyHit;
		}

		return { detected: false };
	}

	/**
	 * Scans the URL query string for MongoDB operator injection patterns.
	 * Checks raw string first (to catch encoded $ with high confidence),
	 * then parsed param names/values (to catch literal bracket notation).
	 *
	 * @param {string} url
	 * @returns {object|null}
	 */
	_scanUrl(url) {
		let rawSearch;
		try {
			rawSearch = new URL(url).search;
		} catch (_) {
			const idx = url.indexOf('?');
			if (idx === -1) return null;
			rawSearch = url.substring(idx);
		}
		if (!rawSearch || rawSearch === '?') return null;

		// 1. Encoded dollar sign in bracket: field[%24ne]=value — high confidence.
		//    Legitimate libraries never encode $ in bracket-notation keys.
		if (ENCODED_DOLLAR_BRACKET.test(rawSearch)) {
			const match = rawSearch.match(ENCODED_DOLLAR_BRACKET);
			return {
				detected: true,
				type: 'NOSQL',
				variant: 'mongo_encoded_operator',
				evidence: match[0],
				confidence: 'high'
			};
		}

		// 2. Parse params and check decoded keys/values for operator patterns.
		let params;
		try {
			params = new URLSearchParams(rawSearch);
		} catch (_) {
			return null;
		}

		for (const [name, value] of params.entries()) {
			// Bracket notation in key: field[$ne], a[b][$gt], etc.
			const bracketMatch = name.match(/\[(\$[a-zA-Z]+)\]/);
			if (bracketMatch) {
				const operator = bracketMatch[1];
				if (MONGO_WHERE.test(operator)) {
					return {
						detected: true,
						type: 'NOSQL',
						variant: 'mongo_where_js',
						evidence: bracketMatch[0],
						confidence: 'high'
					};
				}
				if (MONGO_OPERATORS.test(operator)) {
					return {
						detected: true,
						type: 'NOSQL',
						variant: 'mongo_operator_param',
						evidence: bracketMatch[0],
						confidence: 'medium'
					};
				}
			}
			// $where in a param value (e.g. ?query=$where:function(){...})
			if (value && MONGO_WHERE.test(value)) {
				const match = value.match(MONGO_WHERE);
				return {
					detected: true,
					type: 'NOSQL',
					variant: 'mongo_where_js',
					evidence: match[0],
					confidence: 'high'
				};
			}
		}

		return null;
	}

	/**
	 * Scans a request body for MongoDB operator injection.
	 * Handles JSON bodies (deep key scan) and URL-encoded bodies (bracket notation).
	 *
	 * @param {string} body
	 * @returns {object|null}
	 */
	_scanBody(body) {
		const firstChar = body.charAt(0);

		// JSON body: parse and scan all keys recursively
		if (firstChar === '{' || firstChar === '[') {
			try {
				const parsed = JSON.parse(body);
				if (typeof parsed === 'object' && parsed !== null) {
					return this._scanJsonObject(parsed, 0);
				}
			} catch (_) {
				// Not valid JSON — fall through
			}
		}

		// URL-encoded body: check for bracket notation and encoded $ operators
		if (body.indexOf('=') !== -1) {
			if (ENCODED_DOLLAR_BRACKET.test(body)) {
				const match = body.match(ENCODED_DOLLAR_BRACKET);
				return {
					detected: true,
					type: 'NOSQL',
					variant: 'mongo_encoded_operator',
					evidence: match[0],
					confidence: 'high'
				};
			}
			// Bracket notation with literal $
			const bracketMatch = body.match(/\[(\$[a-zA-Z]+)\]/);
			if (bracketMatch) {
				const operator = bracketMatch[1];
				if (MONGO_WHERE.test(operator)) {
					return {
						detected: true,
						type: 'NOSQL',
						variant: 'mongo_where_js',
						evidence: bracketMatch[0],
						confidence: 'high'
					};
				}
				if (MONGO_OPERATORS.test(operator)) {
					return {
						detected: true,
						type: 'NOSQL',
						variant: 'mongo_operator_param',
						evidence: bracketMatch[0],
						confidence: 'medium'
					};
				}
			}
		}

		return null;
	}

	/**
	 * Recursively scans a parsed JSON object for MongoDB operator keys.
	 * Capped at depth 5 to prevent performance issues on deeply nested payloads.
	 *
	 * @param {object|Array} obj
	 * @param {number} depth
	 * @returns {object|null}
	 */
	_scanJsonObject(obj, depth) {
		if (depth > 5 || typeof obj !== 'object' || obj === null) return null;

		const entries = Array.isArray(obj)
			? obj.map((v, i) => [String(i), v])
			: Object.entries(obj);

		for (const [key, value] of entries) {
			// $where as a JSON key — JS execution on the MongoDB server
			if (MONGO_WHERE.test(key)) {
				return {
					detected: true,
					type: 'NOSQL',
					variant: 'mongo_where_js',
					evidence: key.substring(0, 32),
					confidence: 'high'
				};
			}

			// Other MongoDB operator as a JSON key ($ne, $gt, $regex, etc.)
			if (MONGO_OPERATORS.test(key)) {
				return {
					detected: true,
					type: 'NOSQL',
					variant: 'mongo_operator_body',
					evidence: key.substring(0, 32),
					confidence: 'high'
				};
			}

			// $where appearing in a string value
			if (typeof value === 'string' && MONGO_WHERE.test(value)) {
				const match = value.match(MONGO_WHERE);
				return {
					detected: true,
					type: 'NOSQL',
					variant: 'mongo_where_js',
					evidence: match[0],
					confidence: 'high'
				};
			}

			// Recurse into nested objects/arrays
			if (typeof value === 'object' && value !== null) {
				const nested = this._scanJsonObject(value, depth + 1);
				if (nested) return nested;
			}
		}

		return null;
	}
}

export default NoSQLDetector;
